import React, {
  FC,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import FilterField from '@src/uikit/Tables/Filtration/FilterField';
import TableDynamic from '@uikit/Tables/CustomTable';
import './style.scss';
import { TableSectionProps } from './constants';
import InfiniteTableDynamic from '@uikit/Tables/TableDynamic/InfiniteTableDynamic';
import {
  filterOptionType,
  SelectType,
} from '@uikit/Tables/Filtration/constants';
import { Navigate } from 'react-router-dom';

// --- Deep Equality Check ---

const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;

  // React elements hold a circular `_owner` fiber, compare what they render instead
  if (isValidElement(obj1) || isValidElement(obj2)) {
    return (
      isValidElement(obj1) &&
      isValidElement(obj2) &&
      obj1.type === obj2.type &&
      obj1.key === obj2.key &&
      deepEqual(obj1.props, obj2.props)
    );
  }

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

export interface SortingState {
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

export interface FilterColumn {
  label: string;
  name: string;
  filterKey?: string;
  disabled: boolean;
}

const TableSectionInner: FC<TableSectionProps> = memo(
  ({
    title,
    filteredItems,
    id,
    requestTablePageChange,
    totalPages,
    columns,
    columnsForFilter,
    page,
    totalElements,
    modalName,
    size,
    rows,
    permissions,
    onEditClick,
    sortBy,
    sortDir,
    onAuditClick,
    currentPage,
    onPageChange,
    showTable,
    isLoading,
    setModalState,
    hasCreatePermission,
    handleImageClick,
    activeRowId,
    handleSelectedRow,
    handleDeleteRow,
    tableInfo,
    handleAddNewItem,
    onFieldClick,
    openModal,
    path,
    tableName,
    addBtnTitle,
    onFieldFiltration,
    tableCustomClassName,
    params,
    argument,
    isFilterable = true,
    disableAppliedSelectFilters = false,
    tableIsBig = false,
    hasPagination,
    statusSetter,
    skeletonProps,
    columnsSkeleton,
    titleAdditionalElement,
    arrowIconMember,
    infiniteScroll = false,
    infiniteScrollThreshold,
    dataTestId,
    buttonsClassName = '',
    onUpdate,
    emptyState,
  }) => {
    const [columnsSorting, setColumnsSorting] = useState<any>({});
    const [sortingTable, setSortingTable] = useState<SortingState>(() => ({
      sortBy: sortBy || 'id',
      sortDir: sortDir || 'desc',
    }));
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState<boolean>(false);

    useEffect(() => {
      setSortingTable((prev) => {
        if (prev.sortBy !== sortBy || prev.sortDir !== sortDir) {
          return { sortBy, sortDir };
        }
        return prev;
      });
    }, [sortBy, sortDir]);

    const filterOptions = useMemo(() => {
      const sourceColumns = columnsForFilter || columns;
      if (!sourceColumns) return [];

      const result: Array<filterOptionType> = [];

      sourceColumns.forEach((item) => {
        if (item.isFilterable) {
          result.push({
            label: item.headerName,
            name: item.field,
            filterKey: item.filterKey,
            filterType: item.filterType || '',
            filterSelectName: (item.filterSelectName ||
              item.field) as SelectType,
            filterOptions: item.filterOptions,
            requestByAsync: item.requestByAsync,
          });
        }
      });

      return result;
    }, [columnsForFilter, columns, filteredItems]);

    const handleSetIsFilterPopupOpen = useCallback((value: boolean) => {
      setIsFilterPopupOpen(value);
    }, []);

    const hasReadPermission =
      typeof showTable === 'boolean'
        ? showTable
        : permissions?.abilities
          ? permissions.abilities.includes('Read')
          : true;

    if (!hasReadPermission && !isLoading) {
      return <Navigate to="/no-authorization" replace />;
    }

    return (
      <div
        className="table_section"
        data-test-id={`${dataTestId}-table-section`}
      >
        <FilterField
          label="Filter"
          numberId={id}
          setOpen={handleSetIsFilterPopupOpen} // Use memoized handler
          title={title}
          tableInfo={tableInfo}
          open={isFilterPopupOpen}
          path={path}
          page={page}
          size={size}
          argument={argument}
          isFilterable={isFilterable}
          disableAppliedSelectFilters={disableAppliedSelectFilters}
          buttonsClassName={buttonsClassName}
          // sortingTable={sortingTable}
          //TODO check this and then remove
          sortingTable={{ ...sortingTable, ...params }}
          fieldColumns={filterOptions} // Memoized filter options
          columnsSorting={columnsSorting}
          setColumnsSorting={setColumnsSorting}
          handleAddNewItem={handleAddNewItem}
          setModalState={setModalState}
          hasCreatePermission={hasCreatePermission}
          filteredItems={filteredItems}
          onFieldFiltration={onFieldFiltration!}
          requestTablePageChange={requestTablePageChange}
          tableName={tableName || ''}
          addBtnTitle={addBtnTitle || ''}
          openModal={openModal}
          modalName={modalName}
          titleAdditionalElement={titleAdditionalElement}
          dataTestId={dataTestId}
        />

        {infiniteScroll ? (
          <InfiniteTableDynamic
            tableTitle={title}
            modalName={modalName}
            onAuditClick={onAuditClick}
            abilities={permissions?.abilities}
            sortingTable={sortingTable}
            setSortingTable={setSortingTable}
            columns={columns}
            columnsSorting={columnsSorting}
            openModal={openModal}
            rows={rows}
            size={size}
            path={path}
            tableName={tableName || ''}
            page={page}
            requestTablePageChange={requestTablePageChange}
            totalElements={totalElements}
            params={params}
            argument={argument}
            totalPages={totalPages}
            isLoading={isLoading}
            onEditClick={onEditClick}
            onImageClick={handleImageClick}
            onPageChange={onPageChange}
            currentPage={currentPage}
            activeRowId={activeRowId}
            handleSelectedRow={handleSelectedRow}
            handleDeleteRow={handleDeleteRow}
            onFieldClick={onFieldClick}
            tableCustomClassName={tableCustomClassName}
            tableIsBig={tableIsBig}
            hasPagination={hasPagination}
            statusSetter={statusSetter}
            skeletonProps={skeletonProps}
            arrowIconMember={arrowIconMember}
            scrollThreshold={infiniteScrollThreshold}
          />
        ) : (
          <TableDynamic
            dataTestId={`${dataTestId}-table-dynamic-component`}
            tableTitle={title}
            modalName={modalName}
            onAuditClick={onAuditClick}
            abilities={permissions?.abilities}
            sortingTable={sortingTable}
            setSortingTable={setSortingTable}
            columns={columns}
            columnsSorting={columnsSorting}
            openModal={openModal}
            rows={rows}
            size={size}
            path={path}
            tableName={tableName || ''}
            page={page}
            requestTablePageChange={requestTablePageChange}
            totalElements={totalElements}
            params={params}
            argument={argument}
            totalPages={totalPages}
            isLoading={isLoading}
            onEditClick={onEditClick}
            onImageClick={handleImageClick}
            onPageChange={onPageChange}
            currentPage={currentPage}
            activeRowId={activeRowId}
            handleSelectedRow={handleSelectedRow}
            handleDeleteRow={handleDeleteRow}
            onFieldClick={onFieldClick}
            tableCustomClassName={tableCustomClassName}
            tableIsBig={tableIsBig}
            hasPagination={hasPagination}
            statusSetter={statusSetter}
            skeletonProps={skeletonProps}
            columnsSkeleton={columnsSkeleton}
            arrowIconMember={arrowIconMember}
            onUpdate={onUpdate}
            emptyText={
              // Same check FilterField uses to show applied-filter chips
              (Object.keys(filteredItems?.[0] ?? {}).length > 0 &&
                emptyState?.search) ||
              emptyState?.empty
            }
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => deepEqual(prevProps, nextProps)
);

// Changing `resetKey` (e.g. the active tab or selected role) remounts the table,
// clearing all local filter, sort, page and popup state.
const TableSection: FC<TableSectionProps> = ({ resetKey, ...props }) => (
  <TableSectionInner key={resetKey} {...(props as TableSectionProps)} />
);

export default TableSection;
